import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'widget-radar-grafik',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg [attr.viewBox]="viewBox()" class="radar-svg">
      <g [attr.transform]="merkezTransform()">
        @for (halka of halkalar(); track halka.seviye) {
          <polygon [attr.points]="halka.noktalar" class="radar-halka" />
        }
        @for (eksen of eksenler(); track eksen.idx) {
          <line x1="0" y1="0"
            [attr.x2]="eksen.x" [attr.y2]="eksen.y"
            class="radar-eksen" />
        }
        <polygon
          [attr.points]="veriNoktalari()"
          [style.fill]="renk()"
          [style.fill-opacity]="doldurmaOpakligi()"
          [style.stroke]="renk()"
          class="radar-veri" />
        @for (n of noktalar(); track n.idx) {
          <circle [attr.cx]="n.x" [attr.cy]="n.y" r="3"
            [style.fill]="renk()" class="radar-nokta" />
        }
        @for (eksen of eksenler(); track eksen.idx) {
          <text [attr.x]="eksen.etiketX" [attr.y]="eksen.etiketY"
            [attr.text-anchor]="eksen.anchor"
            [attr.dominant-baseline]="eksen.baseline"
            class="radar-etiket">{{ eksen.etiket }}</text>
        }
      </g>
    </svg>
  `,
  styles: [`
    :host { display: block; }
    .radar-svg { width: 100%; height: auto; }
    .radar-halka {
      fill: none; stroke: currentColor; opacity: 0.12; stroke-width: 0.5;
    }
    .radar-eksen { stroke: currentColor; opacity: 0.15; stroke-width: 0.5; }
    .radar-veri {
      stroke-width: 1.5; stroke-linejoin: round;
      transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .radar-nokta {
      stroke: var(--py-arkaplan, #fff); stroke-width: 1.5;
      transition: cx 0.5s cubic-bezier(0.4, 0, 0.2, 1),
                  cy 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .radar-etiket { font-size: 9px; fill: currentColor; opacity: 0.65; }
  `],
})
export class RadarGrafikComponent {
  veriler = input<{ etiket: string; deger: number }[]>([]);
  maxDeger = input(100);
  renk = input('#1890ff');
  doldurmaOpakligi = input(0.2);
  boyut = input(160);

  private yaricap = computed(() => this.boyut() * 0.35);
  private merkez = computed(() => this.boyut() / 2);

  viewBox = computed(() => `0 0 ${this.boyut()} ${this.boyut()}`);
  merkezTransform = computed(() => `translate(${this.merkez()},${this.merkez()})`);

  private acilar = computed(() => {
    const n = Math.max(this.veriler().length, 3);
    return Array.from({ length: n }, (_, i) => (2 * Math.PI * i) / n - Math.PI / 2);
  });

  halkalar = computed(() => {
    const seviyeSayisi = 5;
    const r = this.yaricap();
    const acilarVal = this.acilar();
    return Array.from({ length: seviyeSayisi }, (_, s) => {
      const oran = (s + 1) / seviyeSayisi;
      const noktalar = acilarVal
        .map(a => `${(Math.cos(a) * r * oran).toFixed(2)},${(Math.sin(a) * r * oran).toFixed(2)}`)
        .join(' ');
      return { seviye: s, noktalar };
    });
  });

  eksenler = computed(() => {
    const v = this.veriler();
    const r = this.yaricap();
    const acilarVal = this.acilar();
    const etiketMesafe = r + 14;

    return acilarVal.map((a, idx) => {
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      const ex = cos * etiketMesafe;
      const ey = sin * etiketMesafe;

      let anchor = 'middle';
      if (cos > 0.3) anchor = 'start';
      else if (cos < -0.3) anchor = 'end';

      let baseline = 'middle';
      if (sin < -0.3) baseline = 'auto';
      else if (sin > 0.3) baseline = 'hanging';

      return {
        idx,
        x: +(cos * r).toFixed(2),
        y: +(sin * r).toFixed(2),
        etiketX: +ex.toFixed(2),
        etiketY: +ey.toFixed(2),
        etiket: v[idx]?.etiket ?? '',
        anchor,
        baseline,
      };
    });
  });

  noktalar = computed(() => {
    const v = this.veriler();
    const r = this.yaricap();
    const max = this.maxDeger() || 1;
    const acilarVal = this.acilar();

    return acilarVal.map((a, idx) => {
      const oran = Math.min((v[idx]?.deger ?? 0) / max, 1);
      return {
        idx,
        x: +(Math.cos(a) * r * oran).toFixed(2),
        y: +(Math.sin(a) * r * oran).toFixed(2),
      };
    });
  });

  veriNoktalari = computed(() =>
    this.noktalar().map(n => `${n.x},${n.y}`).join(' ')
  );
}
